import { api } from "@api";
import {
  createInfiniteQuery,
  createMutation,
  createQuery,
  useQueryClient,
} from "@tanstack/solid-query";
import { type Accessor } from "solid-js";

type EventQuery = Awaited<
  ReturnType<ReturnType<(typeof api)["events"]["byId"]>["get"]>
>;

export const createEventCouponsQuery = (details: { eventId: number }) => {
  return createQuery({
    queryKey: () => ["event", details.eventId, "coupons"],
    queryFn: async () => {
      return api.events.byId(details.eventId).admin.getEventCoupons({});
    },
  });
};

export const createEventQuery = (details: { eventId: number }) => {
  return createQuery<EventQuery>({
    queryKey: () => ["event", details.eventId],
    queryFn: async () => {
      return api.events.byId(details.eventId).get();
    },
  });
};

export type EventQueryResult = NonNullable<
  ReturnType<typeof createEventQuery>["data"]
>;

type PastEventQuery = Awaited<
  ReturnType<ReturnType<(typeof api)["events"]["byId"]>["getPastEvent"]>
>;

export const createPastEventQuery = (details: { eventId: number }) => {
  return createQuery({
    queryKey: () => ["pastevent", details.eventId],
    queryFn: async () => {
      return api.events.byId(details.eventId).getPastEvent();
    },
  });
};

export type PastEventQueryResult = ReturnType<
  typeof createPastEventQuery
>["data"];

export const createPastEventCachedPathsQuery = (details: {
  eventId: number;
}) => {
  return createQuery({
    queryKey: () => ["pastevent", details.eventId, "cached-paths"],
    queryFn: async () => {
      return api.events.byId(details.eventId).getPastEventCachedPaths();
    },
  });
};

export const createPastEventMutation = (details: { eventId: number }) => {
  const client = useQueryClient();
  return createMutation({
    mutationFn: async (
      input: Parameters<
        ReturnType<(typeof api)["events"]["byId"]>["admin"]["editPastEvent"]
      >[0]
    ) => {
      return api.events.byId(details.eventId).admin.editPastEvent(input);
    },
    onSuccess: (_, input) => {
      client.setQueryData<PastEventQuery>(
        ["pastevent", details.eventId],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            ...input,
            retireDate: input.retireDate ? input.retireDate?.toString() : null,
          };
        }
      );
    },
  });
};

export const createEventMutation = (details: { eventId: number }) => {
  const client = useQueryClient();
  return createMutation({
    mutationFn: async (
      input: Parameters<ReturnType<(typeof api)["events"]["byId"]>["update"]>[0]
    ) => {
      return api.events.byId(details.eventId).update(input);
    },
    onSuccess: (_, input) => {
      client.setQueryData<EventQuery>(["event", details.eventId], (old) => {
        if (!old) return old;
        return {
          ...old,
          ...input,
          startTime: input.startTime?.toString() ?? old.startTime,
          endTime: input.endTime?.toString() ?? old.endTime,
          preRallyPartyTime:
            input.preRallyPartyTime?.toString() ?? old.preRallyPartyTime,
          metadata: input.metadata ?? {},
        };
      });
    },
  });
};

export const createLockEventMutation = (details: { eventId: number }) => {
  const client = useQueryClient();
  return createMutation({
    mutationFn: async () => {
      return api.events.byId(details.eventId).lock();
    },
    onSuccess: () => {
      client.setQueryData<EventQuery>(["event", details.eventId], (old) => {
        if (!old) return old;
        return {
          ...old,
          status: "locking",
        };
      });
    },
  });
};

export const createCompleteEventMutation = (details: { eventId: number }) => {
  const client = useQueryClient();
  return createMutation({
    mutationFn: async () => {
      return api.events.byId(details.eventId).complete();
    },
    onSuccess: () => {
      client.setQueryData<EventQuery>(["event", details.eventId], (old) => {
        if (!old) return old;
        return {
          ...old,
          status: "completing",
        };
      });
    },
  });
};

export const createPlayableEventMutation = (details: { eventId: number }) => {
  const client = useQueryClient();
  return createMutation({
    mutationFn: async ({ keepDiversions }: { keepDiversions: boolean }) => {
      return api.events.byId(details.eventId).admin.makePlayable({
        keepDiversions,
      });
    },
    onSuccess: () => {
      client.setQueryData<EventQuery>(["event", details.eventId], (old) => {
        if (!old) return old;
        return {
          ...old,
          status: "playable",
        };
      });
    },
  });
};

export const createAdminUpcomingEventsQuery = () => {
  return createQuery({
    queryKey: () => ["admin", "events", "upcoming"],
    queryFn: async () => await api.events.admin.getCurrent({}),
  });
};

export const createAdminPastEventsQuery = (props: {
  page: Accessor<number>;
}) => {
  return createQuery({
    queryKey: () => ["admin", "events", "past", props.page()],
    queryFn: async () =>
      api.events.admin.getPast({
        page: props.page(),
      }),
  });
};

export const createAdminPlayablePastEventsQuery = (props: {
  page: Accessor<number>;
}) => {
  return createQuery({
    queryKey: () => ["admin", "events", "playablePast", props.page()],
    queryFn: async () =>
      api.events.admin.getPlayablePastEvents({
        page: props.page(),
      }),
  });
};

export const createPlayablePastEventsQuery = (props: {
  page: Accessor<number>;
}) => {
  return createQuery({
    queryKey: () => ["pastevents", "playablePast", props.page()],
    queryFn: async () =>
      api.events.getPlayablePast({
        page: props.page(),
      }),
  });
};

export const createUpcomingEventsQuery = () => {
  return createInfiniteQuery({
    queryKey: () => ["events", "upcoming"],
    queryFn: async ({ pageParam }) =>
      api.events.get({
        past: false,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.nextPage) return lastPage.nextPage;
      return undefined;
    },
  });
  // return createQuery({
  //   queryKey: () => ["events", "upcoming"],
  //   queryFn: async () => api.events.get({ past: "false" }),
  // });
};

export const createUpcomingEventsQueryFlat = () => {
  return createQuery({
    queryKey: () => ["events", "upcoming"],
    queryFn: async () =>
      api.events.get({
        past: false,
        page: 0,
      }),
  });
};

export const createRegisteredEventsQuery = () => {
  return createQuery({
    queryKey: () => ["events", "registered"],
    queryFn: async () => api.users.events.getRecent(),
  });
};

export const createRegisteredAsOwnerOrMemberQuery = () => {
  return createQuery({
    queryKey: () => ["events", "registered", "owner-or-member"],
    queryFn: async () => api.users.events.getRegistered(),
  });
};

export const createRegisteredPastEventsQuery = () => {
  return createQuery({
    queryKey: () => ["events", "registered", "past"],
    queryFn: async () => api.users.events.getPast(),
  });
};

export const createPastEventsQuery = () => {
  return createInfiniteQuery({
    queryKey: () => ["events", "past"],
    queryFn: async ({ pageParam }) =>
      api.events.get({
        past: true,
        page: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      if (lastPage.nextPage) return lastPage.nextPage;
      return undefined;
    },
  });
  // return createQuery({
  //   queryKey: () => ["events", "past"],
  //   queryFn: async () => api.events.get({ past: "true" }),
  // });
};
